import config from '@iotv/config';
import { useEffect, useReducer, useRef, useState } from 'react';
import { getObjectFromS3, s3ListWithCors } from '../../../../data/aws/s3/api';
import { ProcessReceptionAreaRequestStatus } from '../../CloudSideTypes';
const debug = process.env.REACT_APP_DEBUG && false;
/**
 * The S3 bucket name where coverage reports are stored
 */
const coverageBucketName = config.aws.s3.networkCoverage.Name;
const coverageReportReducer = (currentState, kvUpdate) => {
    const { geoSk, reports } = kvUpdate;
    const newState = { ...currentState, [geoSk]: reports };
    debug && console.log(`useCoverage coverageReportReducer updating for ${kvUpdate.geoSk}, newState has keys ${Object.keys(newState)} `);
    return newState;
};
/**
 * Assembles the appropriate `sk` for a {@link CoverageReport}
 */
export const getCoverageReportSk = (cr) => `CoverageReport:${cr.GeoObject.pk}:${cr.timePeriod?.fromDateMs ?? 'never'}:${cr.timePeriod?.toDateMs ?? 'never'}`;
/**
 * Assembles the appropriate `sk` for all {@link CoverageReport}s in the given {@link CoverageReportMap}
 */
export const allCoverageSks = (coverageReportMap) => Object.entries(coverageReportMap)
    .map(([_, reports]) => reports.map((report) => getCoverageReportSk(report)).flat())
    .flat();
/**
 * Retrieves and provides existing {@link CoverageReport}s for {@link CoverageGeoObject}s,
 * and gets any newly produced reports (when indicated in the {@link RequestStatusMap})
 */
export const useCoverage = ({ coverageGeoObjects, requestStatusMap }) => {
    const retrievedKeys = useRef([]);
    const [retrievingReports, setRetrievingReports] = useState(false);
    /**
     * Maps all {@link CoverageGeoObject}s to their retrieved {@link CoverageReport}s
     */
    const [coverageReportMap, dispatchCoverageReports] = useReducer(coverageReportReducer, {});
    debug && console.log(`useCoverage has coverageGeoObjects and requestStatusMap`, { coverageGeoObjects, requestStatusMap });
    /**
     * Fetches {@link CoverageReport}s for {@link CoverageGeoObject}s that don't already have reports via {@link getCoverageReports()}
     */
    const getExistingCoverageReports = (coverageGeoObjects) => coverageGeoObjects.filter((geo) => !coverageReportMap[geo.sk]).map(getCoverageReports);
    /**
     * Currently-selected {@link CoverageReport}s due for display on a map or table, etc.
     */
    const [selectedCoverageReportSks, setSelectedCoverageReportSks] = useState([]);
    /**
     * Hash of all selected {@link CoverageReport}s
     */
    const selectedCoverageReportSksHash = selectedCoverageReportSks.sort().join(',');
    /**
     * Hash of all provided {@link CoverageGeoObject}s
     */
    const allCoverageObjectsSkHash = coverageGeoObjects
        .map((geob) => geob.sk)
        .sort()
        .join(',');
    /**
     * When {@link CoverageGeoObject}s changes, get reports for any {@link CoverageGeoObject} that is not already retrieved
     */
    useEffect(() => {
        debug && console.log(`useCoverage: useEffect on allCoverageObjectsSkHash`, allCoverageObjectsSkHash);
        setRetrievingReports(true);
        Promise.all(getExistingCoverageReports(coverageGeoObjects)).then(() => setRetrievingReports(false));
    }, [allCoverageObjectsSkHash]);
    /** Lists then retrieves, coverage report for the geoObject,  */
    const getCoverageReports = async (coverageGeoObject) => {
        const s3ObjectKeyPrefix = getS3KeyPrefix(coverageGeoObject);
        debug && console.log(`useCoverage getCoverageReports: for ${coverageGeoObject.name} with key ${s3ObjectKeyPrefix}`);
        const s3ContentRecords = [];
        const coverageReports = [];
        let nextToken = undefined;
        do {
            const { err, data } = await s3ListWithCors(s3ObjectKeyPrefix, coverageBucketName, nextToken);
            if (err) {
                console.log(`useCoverage getCoverageReport: threw ${err.message} on s3ListWithCors`);
                break;
            }
            if (data?.Contents) {
                const { NextContinuationToken, Contents } = data;
                s3ContentRecords.push(...Contents);
                nextToken = NextContinuationToken;
            }
        } while (nextToken !== undefined);
        /**
         * Potentially limit how much history is retrieved here?
         */
        const limitToLastNReports = 1;
        // s3ContentList.slice( undefined, limitToLastNReports)
        const validReportRecords = s3ContentRecords.filter((contentRecord) => contentRecord.Key !== undefined);
        const latestReportKey = validReportRecords[validReportRecords.length - 1]?.Key;
        for (let reportRecord of validReportRecords) {
            /**
             * Retrieve the coverage report from the S3 bucket
             */
            const { err, data } = await getObjectFromS3({ Key: reportRecord.Key, bucketIdentifier: coverageBucketName });
            if (err) {
                console.log(`useCoverage: Err getting ${reportRecord.Key}`, err.message);
            }
            else if (data) {
                if (coverageReports) {
                    retrievedKeys.current.push(reportRecord.Key);
                    debug && console.log(`useCoverage: received report for ${data.GeoObject.name}`);
                    const sk = getCoverageReportSk(data);
                    /**
                     * 2023 NOV — observed: after a long time idle, in dev, this threw:
                     * `useCoverage.ts:86 Uncaught (in promise) TypeError: Cannot read properties of undefined (reading 'findIndex') at updateCoverageMapFromRequ - provisonally flagged with if predicate`
                     */
                    const existingIdx = coverageReports.findIndex((coverageReport) => getCoverageReportSk(coverageReport) === sk);
                    if (existingIdx !== -1) {
                        coverageReports.splice(existingIdx, 1);
                    }
                    /**
                     * Add the retrieved coverage report
                     */
                    coverageReports.push(data);
                    /**
                     * If the retrieved coverage report is the latest, and it's not selected, select it
                     */
                    if (reportRecord.Key === latestReportKey && !selectedCoverageReportSks.includes(reportRecord.Key ?? 'never')) {
                        // setSelectedCoverageReportSks((s) => [...s, getCoverageReportSk(data)]);
                    }
                }
                else {
                    console.log(`useCoverage: Unusually error possibly on long idle, no coverage reports available`);
                }
            }
            else {
                console.log(`useCoverage: No data after getting ${reportRecord.Key}`);
            }
        }
        dispatchCoverageReports({ geoSk: coverageGeoObject.sk, reports: coverageReports });
    };
    useEffect(() => {
        debug && console.log(`useCoverage: useEffect on requestStatusMap`, requestStatusMap);
        updateCoverageMapFromRequest();
    }, [requestStatusMap]);
    const updateCoverageMapFromRequest = async () => {
        const retrievableKeys = Object.values(requestStatusMap)
            .filter((requestStatus) => {
            const { status, s3ObjectKey } = requestStatus;
            return status === ProcessReceptionAreaRequestStatus.READY && s3ObjectKey && !retrievedKeys.current.includes(s3ObjectKey);
        })
            .map((requestStatus) => requestStatus.s3ObjectKey);
        for (let Key of retrievableKeys) {
            debug && console.log(`useCoverage: updateCoverageMapFromRequest retrieving ${Key})}`);
            /**
             * Retrieve the coverage report from the S3 bucket
             */
            const { err, data } = await getObjectFromS3({ Key, bucketIdentifier: coverageBucketName });
            if (err) {
                console.log(`useCoverage: Err getting new key ${Key}`, err.message);
            }
            else if (data) {
                retrievedKeys.current.push(Key);
                const geoObjectSk = Key.split('/').slice(0, 2).join(':');
                const coverageReports = coverageReportMap[geoObjectSk];
                const sk = getCoverageReportSk(data);
                const existingIdx = coverageReports.findIndex((cr) => getCoverageReportSk(cr) === sk);
                if (existingIdx !== -1) {
                    coverageReports.splice(existingIdx, 1);
                }
                coverageReports.push(data);
                dispatchCoverageReports({ geoSk: geoObjectSk, reports: coverageReports });
                /**
                 * Select the retrieved coverage report for immediate display
                 * We use a callback function to allow us to accurate select each report without waiting for rerenders to sync
                 */
                setSelectedCoverageReportSks((s) => [...s, getCoverageReportSk(data)]);
            }
            else {
                console.log(`useCoverage: No data after getting new Key ${Key}`);
            }
        }
    };
    debug && console.log(`useCoverage: selectedCoverageReportSks`, selectedCoverageReportSks);
    return {
        retrievingReports,
        coverageReportMap,
        selectedReports: selectedCoverageReportSks,
        setSelectedReports: setSelectedCoverageReportSks,
        selectedReportsHash: selectedCoverageReportSksHash,
    };
};
/**
 * Derived from [NetworkSystem/NetworkData/.../networkCoverage/storage/persist](./../../../../../../../lsc/NetworkSystem/NetworkData/src/HttpMethods/networkCoverage/storage/persist.ts)
 */
const getS3KeyPrefix = (coverageGeoObject) => {
    /** form: GeoObject.sk/Network ?? ALL/ fromTime split into YY -> HR, durationMs  */
    const KeyParts = [...coverageGeoObject.sk.split(':')];
    return KeyParts.join('/');
};
